import React, { useCallback, useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { toast } from 'sonner';
import Honeybadger from '@honeybadger-io/js';

import { deserializer } from '../../../../../shared/utils/deserializer';
import { usePagination, useSearchBar } from '../../../../shared/hooks';
import {
  fetchConversation as apiFetchConversation,
  fetchConversations as apiFetchConversations,
} from '../../services/conversationService';
import ConversationsContext from './ConversationsContext';

const ConversationsProvider = ({ children }) => {
  const [activeConversation, setActiveConversation] = useState(null);
  const [conversationsList, setConversationsList] = useState([]);
  const [isLoadingActive, setIsLoadingActive] = useState(true);
  const [isLoadingList, setIsLoadingList] = useState(true);

  const { handlePageChange, pagination, updatePagination } = usePagination();
  const { handleSearchChange, searchInput, searchQuery } = useSearchBar();

  const activeConversationId = activeConversation?.id;

  const handleConversationClick = useCallback((conversation) => {
    setActiveConversation(conversation);
  }, []);

  useEffect(() => {
    const fetchActiveConversation = async () => {
      if (!activeConversationId) return;

      setIsLoadingActive(true);

      try {
        const response = await apiFetchConversation(activeConversationId);
        const deserializedConversation = await deserializer.deserialize(response);

        setActiveConversation(deserializedConversation);
      } catch (error) {
        toast.error(error.message);
        Honeybadger.notify(error);
      } finally {
        setIsLoadingActive(false);
      }
    };

    fetchActiveConversation();
  }, [activeConversationId]);

  useEffect(() => {
    const fetchConversationsList = async () => {
      setIsLoadingList(true);

      try {
        const response = await apiFetchConversations({
          subject: searchQuery,
          page: pagination.currentPage,
          perPage: pagination.perPage,
        });

        const deserializedConversations = await deserializer.deserialize(response);
        const activeConversation = deserializedConversations[0];

        // TODO: Remove once :isRead and :tags have been added.
        deserializedConversations.forEach((conversation) => {
          conversation.lastMessage.isRead = true;
          conversation.tags = ['meeting', 'work', 'important'];
        });

        setConversationsList(deserializedConversations);
        setActiveConversation(activeConversation);
        updatePagination(response.meta.pagination);
      } catch (error) {
        toast.error(error.message);
        Honeybadger.notify(error);
      } finally {
        setIsLoadingList(false);
      }
    };

    fetchConversationsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.perPage, searchQuery]);

  const contextValue = useMemo(
    () => ({
      activeConversation,
      conversationsList,
      handleConversationClick,
      handlePageChange,
      handleSearchChange,
      isLoadingActive,
      isLoadingList,
      pagination,
      searchInput,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      activeConversation,
      conversationsList,
      handleConversationClick,
      isLoadingActive,
      isLoadingList,
      pagination,
      searchInput,
    ]
  );

  return (
    <ConversationsContext.Provider value={contextValue}>
      {children}
    </ConversationsContext.Provider>
  );
};

ConversationsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ConversationsProvider;
